import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Mail } from 'lucide-react'
import { Button, Spinner } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { authService } from '@/services/auth'
import { ExistingUserForm, NewUserForm } from './InvitePageForms'

interface InvitationInfo {
  valid: boolean
  email?: string
  organizationName?: string
  role?: string
  inviterName?: string
  expiresAt?: string
  requiresVerificationCode: boolean
  userExists?: boolean
  errorCode?: string
}

function getErrorMessageKey(errorCode?: string): string {
  switch (errorCode) {
    case 'invalid_token':
      return 'invite.errorInvalidToken'
    case 'invitation_expired':
      return 'invite.errorExpired'
    case 'invitation_already_accepted':
      return 'invite.errorAlreadyAccepted'
    case 'network_error':
      return 'invite.errorNetwork'
    default:
      return 'invite.errorGeneric'
  }
}

export const InvitePage = () => {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const token = searchParams.get('token')

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('invite.errorNoToken'))
        setIsLoading(false)
        return
      }

      try {
        const info = await authService.validateInvitation(token)
        setInvitationInfo(info)

        if (!info.valid) {
          setError(t(getErrorMessageKey(info.errorCode)))
          setIsLoading(false)
          return
        }

        if (info.userExists && !info.requiresVerificationCode) {
          sessionStorage.setItem('invitation_token', token)
          // The sign-in page names no app: auth-service lands the member
          // (a Guest in the chat app, ADR-20260930 D7).
          setTimeout(() => navigate('/login'), 1500)
        } else {
          setIsLoading(false)
        }
      } catch {
        setError(t('invite.errorNetwork'))
        setIsLoading(false)
      }
    }

    validateToken()
  }, [token, navigate, t])

  // Redirecting existing user (no verification needed)
  if (isLoading && invitationInfo?.userExists && !invitationInfo?.requiresVerificationCode) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="status">
          <div className="lp-badge lp-badge-brand">
            <Mail size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('invite.titleDefault')}</h2>
          <hr className="lp-state-divider" />
          <Spinner size="lg" />
          <p className="lp-state-msg">{t('invite.processing')}</p>
          <p className="lp-note">{t('invite.redirectingToLogin')}</p>
        </div>
      </AuthSplitLayout>
    )
  }

  // Error state (invalid invitation)
  if (error && !invitationInfo?.valid) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="alert">
          <div className="lp-badge lp-badge-brand">
            <CircleAlert size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('invite.errorTitle')}</h2>
          <p className="lp-state-msg">{error}</p>
          <hr className="lp-state-divider" />
          <Button variant="outline" onClick={() => navigate('/login')} className="lp-btn-social">
            {t('invite.goToLogin')}
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <AuthSplitLayout>
        <div className="lp-state" role="status">
          <Spinner size="lg" />
          <p className="lp-state-msg">{t('invite.validating')}</p>
        </div>
      </AuthSplitLayout>
    )
  }

  // Existing user - verification code form
  if (invitationInfo?.userExists) {
    return (
      <AuthSplitLayout>
        <ExistingUserForm token={token!} info={invitationInfo} />
      </AuthSplitLayout>
    )
  }

  // New user - registration form
  return (
    <AuthSplitLayout>
      <NewUserForm token={token!} info={invitationInfo!} />
    </AuthSplitLayout>
  )
}

export default InvitePage
